// ---------------------------------------------------------------------------
// A4 — **THE MEMBERSHIP PAGE'S VALUE STACK.**
//
// Her order: *her designed page gains the plain what-you-get list per tier — every room, every
// reading class, Ask Lala turns a day, the community — so $39 lands against a visible pile of value
// rather than a bare price. **Her design's reserved spaces govern. No redesign. An ADDITION.***
//
// >>> **MEASURED BEFORE A LINE WAS WRITTEN (R25/R180), AND THE WHOLE STACK WAS ALREADY ON THE WIRE.**
// <<< `tierContents` in `server/tiers.js` has always returned `unlocks` (a member-facing label per
// feature), `inheritsLabel` (*"Everything in Newbie"*) and `lalaTurnsPerDay`, and its own comment
// says what they are for: *"this is what the membership page and the purple box both render, so what
// she is shown is what she is sold."* All three are TYPED on the client in `src/lib/tier-catalog.ts`
// — **and rendered by NOTHING.** `MembershipPage.tsx`'s own header claims the opposite in as many
// words (*"unlocks PLUS 'everything in' the tier below, spelled out"*), which is R121/R122's class:
// documentation read as installation. **So this round is the SURFACE, and there is no new server
// work, no migration and no second answer to what a tier includes** (R28/R29).
//
// >>> **AND HER *reserved spaces govern* CLAUSE IS WHY THE STACK IS A SIBLING BELOW HER ARTWORK
// RATHER THAN A PANEL INSIDE IT.** <<< Measured off her own pixels: her three desktop cards are
// **54%..73% painted**, and the biggest clean gap inside any of them is **8.29% on Apprentice,
// 2.98% on Newbie and 1.91% on Vet** — 18px on the most expensive tier, the one that most needs a
// value stack. On the phone it is worse (4.07% / 1.79% / **0.84%**). **There is nowhere inside her
// cards to put a list that does not paint over her art**, so the ADDITION goes where the checkout
// note and the way home already live: OUTSIDE `.memberart`. That is not a preference — anything in
// normal flow inside `.memberart__tiers` grows a box every tier target is an `inset: 0` percentage
// of, and slides all three controls off her painted cards. **This house has shipped that exact
// defect three times in two days** and the component's own comment records it.
//
// **IT IMPORTS NOTHING**, which is the entry requirement that lets a `.mjs` gate LOAD and RUN it.
// ---------------------------------------------------------------------------

/**
 * >>> **THE STACK IS THE SERVER'S OWN COMPOSITION, RE-ORDERED AND NEVER RE-DERIVED.** <<<
 *
 * `inheritsLabel` already says *"Everything in Newbie"*, so cumulativeness is a fact the server
 * composed and this law must not compute a second time (R28/R29) — the copy that drifted would be
 * the one on the page that asks for money, and W1-5 measured what two spellings of a price cost this
 * house once already.
 *
 * The FREE layer rides every tier, because it really is hers at every price and a stack that omitted
 * it would under-sell the pile her clause is about. It is named as its own group rather than merged
 * into the tier's own lines, or a member reads her signup readings as something $39 bought her.
 */
export function valueStackFor(tier, catalog) {
  const key = typeof tier === "string" ? tier.trim().toLowerCase() : "";
  if (!key) return null;
  const ladder = Array.isArray(catalog?.ladder) ? catalog.ladder : null;
  // R18/R65 — a catalogue that has NOT ARRIVED is not an empty one. `null` here is *we have not been
  // told*, and the surface holds still on it; an entry with no lines is an ANSWER and draws nothing.
  if (!ladder) return null;
  const row = ladder.find((t) => String(t?.tier || "").toLowerCase() === key);
  if (!row) return null;

  const groups = [];
  const free = Array.isArray(catalog?.freeLayer?.unlocks) ? catalog.freeLayer.unlocks : [];
  const freeLines = lines(free);
  if (freeLines.length) {
    groups.push({ kind: "free", label: String(catalog.freeLayer.label || ""), lines: freeLines });
  }
  const own = lines(row.unlocks);
  if (own.length) groups.push({ kind: "tier", label: "", lines: own });

  return {
    tier: key,
    // **THE INHERITANCE IS A LINE OF ITS OWN AND NEVER A HEADING**, because it is a promise about
    // the tier BELOW rather than a section of this one's list.
    inherits: typeof row.inheritsLabel === "string" ? row.inheritsLabel.trim() : "",
    // >>> **PRESENCE, NEVER COERCION, AND ON THIS FIELD IT DECIDES WHETHER A PROMISE IS MADE AT ALL
    // (R53/R136/R137).** <<< `Number(true)` is 1 and `Number("40")` is 40, and this arrives off JSON.
    // A tier with NO turns draws NO turns line: Ask Lala is APPRENTICE by standing law, so Newbie's
    // real answer is 0 — and *"0 Ask Lala turns a day"* on the $39 card is a promise of nothing
    // dressed as a feature (R18: *she has none* and *she has some* are different facts).
    turns: typeof row.lalaTurnsPerDay === "number" && Number.isFinite(row.lalaTurnsPerDay)
      && row.lalaTurnsPerDay > 0 ? Math.floor(row.lalaTurnsPerDay) : 0,
    groups,
  };
}

function lines(rows) {
  const out = [];
  if (!Array.isArray(rows)) return out;
  for (const r of rows) {
    if (!r || typeof r !== "object") continue;
    const label = typeof r.label === "string" ? r.label.trim() : "";
    // **A FEATURE WITH NO LABEL DRAWS NOTHING RATHER THAN ITS KEY** (R25's `DRAWER_ICONS` ruling):
    // `room:seal-creator` on the page that asks for money is machinery, which S1-A/R117 forbids any
    // member-facing line from naming.
    if (!label) continue;
    if (!out.some((x) => x.label === label)) out.push({ key: String(r.key || ""), label });
  }
  return out;
}

/**
 * Every tier's stack, in the ladder's own order — so a fourth tier joins by EXISTING rather than by
 * somebody remembering, and the order on her page is the order the server sells them in (R11/R31).
 */
export function valueStacks(catalog) {
  const ladder = Array.isArray(catalog?.ladder) ? catalog.ladder : null;
  if (!ladder) return null;
  return ladder
    .map((t) => valueStackFor(String(t?.tier || ""), catalog))
    .filter((s) => s && (s.groups.length > 0 || s.inherits));
}

/** What a stack really promises, for the fixture and for anything that needs to count it. */
export function stackLineCount(stack) {
  if (!stack || !Array.isArray(stack.groups)) return 0;
  return stack.groups.reduce((n, g) => n + (Array.isArray(g.lines) ? g.lines.length : 0), 0);
}
